import * as XLSX from 'xlsx-js-style';

const HEADERS = [
  'SR',
  'PARTICULARS',
  'DIMENSIONS',
  'SIZE',
  'UNIT',
  'QUANTITY',
  'STATUS',
  'REMARKS',
  'STATUS UPDATED BY',
];
const COLUMN_COUNT = HEADERS.length;
const EMPTY_ROW = Array(COLUMN_COUNT).fill('');

const solid = (rgb) => ({ patternType: 'solid', fgColor: { rgb } });

const grid = (color) => ({
  top: { style: 'thin', color: { rgb: color } },
  bottom: { style: 'thin', color: { rgb: color } },
  left: { style: 'thin', color: { rgb: color } },
  right: { style: 'thin', color: { rgb: color } },
});

const titleStyle = {
  font: { name: 'Calibri', sz: 26, bold: true, color: { rgb: 'FFFFFF' } },
  fill: solid('17365D'),
  alignment: { horizontal: 'center', vertical: 'center' },
  border: grid('17365D'),
};

const headerStyle = {
  font: { name: 'Calibri', sz: 11, bold: true, underline: true, color: { rgb: 'FFFFFF' } },
  fill: solid('17365D'),
  alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  border: grid('17365D'),
};

const sectionStyle = {
  font: { name: 'Calibri', sz: 12, bold: true, color: { rgb: 'FFFFFF' } },
  fill: solid('2F75B5'),
  alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  border: grid('2F75B5'),
};

const STATUS_COLUMN = 6;

const statusTone = {
  VERIFIED: { fill: 'ECFDF3', font: '067647', border: '6CE9A6' },
  DISCREPANCY: { fill: 'FEF3F2', font: 'B42318', border: 'FDA29B' },
  EXCEEDED: { fill: 'FFFAEB', font: 'B54708', border: 'FEC84B' },
};

const statusCellStyle = (label) => {
  const tone = statusTone[label];
  if (!tone) return itemStyle(STATUS_COLUMN);
  return {
    font: { name: 'Calibri', sz: 11, bold: true, color: { rgb: tone.font } },
    fill: solid(tone.fill),
    alignment: { horizontal: 'center', vertical: 'center' },
    border: grid(tone.border),
  };
};

const itemStyle = (column) => ({
  font: { name: 'Calibri', sz: 11, color: { rgb: '1F2937' } },
  fill: solid('FFFFFF'),
  alignment: {
    horizontal: column === 1 || column === 2 || column === 7 || column === 8 ? 'left' : 'center',
    vertical: 'center',
    wrapText: column === 1 || column === 2 || column === 7,
  },
  border: grid('D0D4D9'),
});

const sheetName = (name, used) => {
  const cleaned =
    String(name || 'Location')
      .replace(/[\\/?*[\]:]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 31) || 'Location';
  let next = cleaned;
  let index = 2;
  while (used.has(next)) {
    const suffix = ` ${index}`;
    next = `${cleaned.slice(0, 31 - suffix.length)}${suffix}`;
    index += 1;
  }
  used.add(next);
  return next;
};

const text = (value) => {
  if (value === undefined || value === null) return '';
  return String(value);
};

const rowHeight = (kind, label = '') => {
  if (kind === 'title') return 42;
  if (kind === 'header') return 22;
  if (kind === 'gap') return 10;
  const lines = Math.max(1, Math.ceil(String(label).length / 48));
  return Math.min(48, Math.max(kind === 'section' ? 24 : 20, lines * 16));
};

const statusText = (value) => {
  const status = String(value || '').trim().toLowerCase();
  if (status === 'verified') return 'VERIFIED';
  if (status === 'discrepancy') return 'DISCREPANCY';
  if (status === 'exceeded') return 'EXCEEDED';
  return '';
};

const buildSheet = (location, eventName) => {
  const rows = [Array(COLUMN_COUNT).fill(''), [...HEADERS]];
  rows[0][0] = eventName || '';
  const kinds = ['title', 'header'];
  const labels = [eventName || '', ''];

  (location.sections || []).forEach((section, index) => {
    if (index > 0) {
      rows.push([...EMPTY_ROW]);
      kinds.push('gap');
      labels.push('');
    }
    const sectionRow = [...EMPTY_ROW];
    sectionRow[0] = text(section.code).toUpperCase();
    sectionRow[1] = text(section.title).toUpperCase();
    rows.push(sectionRow);
    kinds.push('section');
    labels.push(section.title);
    (section.rows || []).forEach((row) => {
      const size = String(row.size ?? '').trim();
      rows.push([
        text(row.sr).toUpperCase(),
        text(row.particulars).toUpperCase(),
        text(row.dimensions).toUpperCase(),
        size ? size.toUpperCase() : '-',
        text(row.unit).toUpperCase(),
        text(row.quantity).toUpperCase(),
        statusText(row.status),
        text(row.remarks).toUpperCase(),
        text(row.statusUpdatedBy).toUpperCase() || '-',
      ]);
      kinds.push('item');
      labels.push(row.particulars);
    });
  });

  const sheet = XLSX.utils.aoa_to_sheet(rows);
  rows.forEach((row, rowIndex) => {
    row.forEach((_, column) => {
      const address = XLSX.utils.encode_cell({ r: rowIndex, c: column });
      if (!sheet[address]) sheet[address] = { v: '', t: 's' };
      const kind = kinds[rowIndex];
      if (kind === 'title') sheet[address].s = titleStyle;
      else if (kind === 'header') sheet[address].s = headerStyle;
      else if (kind === 'section') sheet[address].s = sectionStyle;
      else if (kind === 'item' || kind === 'item-alt') {
        sheet[address].s =
          column === STATUS_COLUMN
            ? statusCellStyle(sheet[address].v)
            : itemStyle(column);
      }
    });
  });

  sheet['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: COLUMN_COUNT - 1 } },
  ];
  sheet['!cols'] = [
    { wch: 10 },
    { wch: 42 },
    { wch: 22 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
    { wch: 16 },
    { wch: 28 },
    { wch: 22 },
  ];
  sheet['!rows'] = kinds.map((kind, index) => ({
    hpt: rowHeight(kind === 'item-alt' ? 'item' : kind, labels[index]),
  }));
  sheet['!freeze'] = { xSplit: 0, ySplit: 2 };
  return sheet;
};

export const downloadElementReport = ({ eventName, locations, fileName }) => {
  const workbook = XLSX.utils.book_new();
  const usedNames = new Set();
  locations.forEach((location) => {
    XLSX.utils.book_append_sheet(
      workbook,
      buildSheet(location, eventName),
      sheetName(location.location, usedNames),
    );
  });
  XLSX.writeFile(workbook, fileName || 'element-report.xlsx');
};
